import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  IconButton,
  Paper,
  Popover,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { useDealSubAssetsSelector } from "store";
import { AmortizationColumn, AmortizationData, AmortizationRow } from "store/types";
import { formatNumber } from "utils";

import { AmortizationTableRow } from "./AmortizationTableRow";

type AmortizationTableProps = {
  table: AmortizationData["table"];
  rows?: AmortizationRow[];
  onMenuOpen?: (event: MouseEvent<HTMLElement>, index: number) => void;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  onExplanationSelect?: (explanation: string | null) => void;
  boundaryRef?: React.RefObject<HTMLElement>;
  columnVisibility?: Record<string, boolean>;
  sx?: SxProps;
};

type SelectedCell = { cellKey: string; value: number; explanation: string };

type RowFragmentProps = {
  row: AmortizationRow;
  index: number;
  columns: AmortizationColumn[];
  showExpandCell: boolean;
  isExpanded: boolean;
  showTodayLine?: boolean;
  onExpandClick: (row: AmortizationRow) => void;
  onMenuOpen?: (event: MouseEvent<HTMLElement>, index: number) => void;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  onSubDealClick: (e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => void;
  onCellExplanationClick?: (cellKey: string, explanation: string, value: number, isCtrl: boolean) => void;
  selectedCellKeys?: string[];
  primaryCellKey?: string;
  formatCellValue: (value: unknown, column: AmortizationColumn) => string | number;
};

type ChildRowProps = {
  childRow: AmortizationRow;
  childIndex: number;
  parentIndex: number;
  columns: AmortizationColumn[];
  showExpandCell: boolean;
  isLastChild: boolean;
  onChildMenuOpen?: (event: MouseEvent<HTMLElement>, index: number, childIndex: number) => void;
  onSubDealClick: (e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => void;
  onCellExplanationClick?: (cellKey: string, explanation: string, value: number, isCtrl: boolean) => void;
  selectedCellKeys?: string[];
  primaryCellKey?: string;
  formatCellValue: (value: unknown, column: AmortizationColumn) => string | number;
};

const ChildRow: React.FC<ChildRowProps> = React.memo(
  ({
    childRow,
    childIndex,
    parentIndex,
    columns,
    showExpandCell,
    isLastChild,
    onChildMenuOpen,
    onSubDealClick,
    onCellExplanationClick,
    selectedCellKeys,
    primaryCellKey,
    formatCellValue
  }) => {
    const handleChildMenuOpen = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onChildMenuOpen?.(e, parentIndex, childIndex);
      },
      [onChildMenuOpen, parentIndex, childIndex]
    );

    return (
      <AmortizationTableRow
        row={childRow}
        columns={columns}
        isChild={true}
        showExpandCell={showExpandCell}
        showBorderBottom={isLastChild}
        onMenuOpen={handleChildMenuOpen}
        onSubDealClick={onSubDealClick}
        onCellExplanationClick={onCellExplanationClick}
        selectedCellKeys={selectedCellKeys}
        primaryCellKey={primaryCellKey}
        formatCellValue={formatCellValue}
      />
    );
  }
);

const RowFragment: React.FC<RowFragmentProps> = React.memo(
  ({
    row,
    index,
    columns,
    showExpandCell,
    isExpanded,
    showTodayLine,
    onExpandClick,
    onMenuOpen,
    onChildMenuOpen,
    onSubDealClick,
    onCellExplanationClick,
    selectedCellKeys,
    primaryCellKey,
    formatCellValue
  }) => {
    const handleMenuOpen = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onMenuOpen?.(e, index);
      },
      [onMenuOpen, index]
    );

    const expandButton = useMemo(() => {
      if (!row.children) return undefined;
      return (
        <IconButton onClick={() => onExpandClick(row)}>
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      );
    }, [isExpanded, row, onExpandClick]);

    return (
      <>
        <AmortizationTableRow
          row={row}
          columns={columns}
          showExpandCell={showExpandCell}
          expandButton={expandButton}
          onMenuOpen={handleMenuOpen}
          onSubDealClick={onSubDealClick}
          onCellExplanationClick={onCellExplanationClick}
          selectedCellKeys={selectedCellKeys}
          primaryCellKey={primaryCellKey}
          formatCellValue={formatCellValue}
          showBorderBottom={!isExpanded}
          showTodayLine={showTodayLine}
        />
        {isExpanded &&
          row.children?.map((childRow, childIndex) => (
            <ChildRow
              key={`${row.date}-child-${childIndex}`}
              childRow={childRow}
              childIndex={childIndex}
              parentIndex={index}
              columns={columns}
              showExpandCell={showExpandCell}
              isLastChild={childIndex === row.children!.length - 1}
              onChildMenuOpen={onChildMenuOpen}
              onSubDealClick={onSubDealClick}
              onCellExplanationClick={onCellExplanationClick}
              selectedCellKeys={selectedCellKeys}
              primaryCellKey={primaryCellKey}
              formatCellValue={formatCellValue}
            />
          ))}
      </>
    );
  }
);

export const AmortizationTable: React.FC<AmortizationTableProps> = ({
  table,
  rows: rowsProp,
  onMenuOpen,
  onChildMenuOpen,
  onExplanationSelect,
  boundaryRef,
  columnVisibility: controlledColumnVisibility,
  sx
}) => {
  const { t } = useTranslation("debt");
  const subAssets = useDealSubAssetsSelector();
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [selectedCells, setSelectedCells] = useState<SelectedCell[]>([]);
  const selectedCellKeys = useMemo(() => selectedCells.map((c) => c.cellKey), [selectedCells]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: globalThis.MouseEvent) => {
      const boundary = boundaryRef?.current ?? containerRef.current;
      if (boundary && !boundary.contains(e.target as Node)) {
        setSelectedCells([]);
        onExplanationSelect?.(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onExplanationSelect, boundaryRef]);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverChildren, setPopoverChildren] = useState<AmortizationRow[]>([]);
  const formatDate = useFormatDateHook();

  const columnVisibility = controlledColumnVisibility ?? {};

  const columns = useMemo(() => {
    if (Object.keys(columnVisibility).length > 0) {
      return table.columns.filter((col) => columnVisibility[col.key] !== false);
    }
    return table.columns.filter((col) => col.visible);
  }, [table.columns, columnVisibility]);

  const rows = useMemo(() => rowsProp ?? table.rows.filter((row) => !row.is_report), [rowsProp, table.rows]);

  const todayLineIndex = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    for (let i = 0; i < rows.length - 1; i++) {
      if (rows[i].date.slice(0, 10) <= todayStr && rows[i + 1].date.slice(0, 10) > todayStr) {
        return i;
      }
    }
    return -1;
  }, [rows]);

  const getRowId = (row: AmortizationRow) => `${row.segmentIds.join(",")}-${row.date}`;

  const handleExpandClick = useCallback((row: AmortizationRow) => {
    const rowId = getRowId(row);
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const handleSubDealClick = useCallback((e: MouseEvent<HTMLElement>, children: AmortizationRow[]) => {
    setPopoverAnchor(e.currentTarget);
    setPopoverChildren(children);
  }, []);

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverChildren([]);
  };

  const isPopoverOpen = Boolean(popoverAnchor);

  const handleCellExplanationClick = useCallback(
    (cellKey: string, explanation: string, value: number, isCtrl: boolean) => {
      setSelectedCells((prev) => {
        let next: SelectedCell[];
        if (isCtrl) {
          const exists = prev.some((c) => c.cellKey === cellKey);
          next = exists ? prev.filter((c) => c.cellKey !== cellKey) : [...prev, { cellKey, value, explanation }];
        } else {
          next = prev.length === 1 && prev[0].cellKey === cellKey ? [] : [{ cellKey, value, explanation }];
        }
        onExplanationSelect?.(next[next.length - 1]?.explanation ?? null);
        return next;
      });
    },
    [onExplanationSelect]
  );

  const formatCellValue = useCallback(
    (value: unknown, column: AmortizationColumn): string | number => {
      if (column.type === "date") {
        return formatDate({ date: value as string });
      }
      if (column.suffix && typeof value === "number") {
        const formattedNumber = formatNumber(value);
        return column.suffix === "%" ? `${formattedNumber}${column.suffix}` : `${column.suffix}${formattedNumber}`;
      }
      return value as string | number;
    },
    [formatDate]
  );

  return (
    <>
      <TableContainer
        ref={containerRef}
        component={Paper}
        elevation={0}
        sx={{
          overflow: "auto",
          border: "1px solid #DBE8F5",
          borderRadius: "8px",
          minHeight: "160px",
          "& .MuiTable-root": {
            borderCollapse: "collapse"
          },
          ...sx
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {subAssets.length > 0 && (
                <TableCell
                  sx={{ width: "40px", minWidth: "40px", padding: "6px 6px", position: "sticky", left: 0, zIndex: 4 }}
                />
              )}
              <TableCell
                sx={{
                  width: "32px",
                  minWidth: "32px",
                  padding: "6px",
                  textAlign: "center",
                  position: "sticky",
                  left: subAssets.length > 0 ? 40 : 0,
                  zIndex: 4
                }}
              />
              {columns.map((column, colIndex) => (
                <TableCell
                  key={column.key}
                  sx={
                    colIndex === 0
                      ? {
                          position: "sticky",
                          left: subAssets.length > 0 ? 72 : 32,
                          zIndex: 4
                        }
                      : undefined
                  }
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const rowId = getRowId(row);
              const isExpanded = expandedRows[rowId];
              const rowPrefix = `${row.segmentIds.join(",")}-${row.date}-`;
              const globalPrimary = selectedCellKeys[selectedCellKeys.length - 1];
              const hasAnySelection = selectedCellKeys.some((k) => {
                if (k.startsWith(rowPrefix)) return true;
                return row.children?.some((child) => k.startsWith(`${child.segmentIds.join(",")}-${child.date}-`));
              });
              const rowSelectedKeys = hasAnySelection ? selectedCellKeys : undefined;
              const rowPrimaryKey = hasAnySelection ? globalPrimary : undefined;

              return (
                <RowFragment
                  key={row.date}
                  row={row}
                  index={index}
                  columns={columns}
                  showExpandCell={subAssets.length > 0}
                  isExpanded={isExpanded}
                  showTodayLine={index === todayLineIndex}
                  onExpandClick={handleExpandClick}
                  onMenuOpen={onMenuOpen}
                  onChildMenuOpen={onChildMenuOpen}
                  onSubDealClick={handleSubDealClick}
                  onCellExplanationClick={handleCellExplanationClick}
                  selectedCellKeys={rowSelectedKeys}
                  primaryCellKey={rowPrimaryKey}
                  formatCellValue={formatCellValue}
                />
              );
            })}
          </TableBody>
        </Table>
        <Popover
          open={isPopoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left"
          }}
          sx={{
            "& .MuiPopover-paper": {
              padding: "8px 12px",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
            }
          }}
        >
          <Stack spacing={1}>
            {popoverChildren.map((child, index) => (
              <NavigateLink
                key={index}
                allowNewTab={true}
                route="dealDashboard"
                params={{ deal_id: String(child.deal_id) }}
                style={{ color: "#26a6ae", fontSize: "14px" }}
              >
                {child.sub_deal}
              </NavigateLink>
            ))}
          </Stack>
        </Popover>
      </TableContainer>
      {selectedCells.length > 1 &&
        (() => {
          const values = selectedCells.map((c) => c.value);
          const sum = values.reduce((a, b) => a + b, 0);
          const avg = sum / values.length;
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 0.75, gap: 3, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                AVERAGE: <strong>{formatNumber(avg)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                COUNT: <strong>{values.length}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                SUM: <strong>{formatNumber(sum)}</strong>
              </Typography>
            </Box>
          );
        })()}
    </>
  );
};
